import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Snackbar, Alert, MenuItem, Chip, Stack,
  Avatar, Grid, FormControl, InputLabel, Select, InputAdornment,
  Divider,
} from '@mui/material';
import {
  CardGiftcard, Delete, Edit, Search as SearchIcon,
  Clear as ClearIcon, Refresh as RefreshIcon, Paid as PaidIcon,
  HourglassEmpty as PendingIcon, ErrorOutline as FailedIcon,
  Replay as RefundIcon, ReceiptLong as ReceiptIcon,
  CheckCircle as CheckIcon, TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import authService from '../Auth/AuthService';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

// ---------- Helpers ----------

const statusMeta = (status = '') => {
  const s = String(status).toLowerCase();
  switch (s) {
    case 'paid':
      return { label: 'Paid', color: '#059669', bg: '#dcfce7', icon: <CheckIcon fontSize="small" /> };
    case 'pending':
      return { label: 'Pending', color: '#d97706', bg: '#fef3c7', icon: <PendingIcon fontSize="small" /> };
    case 'failed':
      return { label: 'Failed', color: '#dc2626', bg: '#fee2e2', icon: <FailedIcon fontSize="small" /> };
    case 'refunded':
      return { label: 'Refunded', color: '#7c3aed', bg: '#ede9fe', icon: <RefundIcon fontSize="small" /> };
    default:
      return { label: status || '—', color: '#64748b', bg: '#f1f5f9', icon: <ReceiptIcon fontSize="small" /> };
  }
};

const currencySymbol = (currency = '') => {
  const c = String(currency).toUpperCase();
  if (c === 'INR') return '₹';
  if (c === 'USD') return '$';
  if (c === 'EUR') return '€';
  if (c === 'GBP') return '£';
  return c ? `${c} ` : '';
};

const formatMoney = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  return `${currencySymbol(currency)}${Number(amount).toLocaleString()}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------- Page ----------

const OrderView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const fetchedRef = useRef(false);

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [saving, setSaving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ---------- Data ----------
  const loadData = async () => {
    try {
      setLoading(true);
      const url = authService.pathUrl('GET', '/payment/orders');
      const res = await authService.getAuth(url);
      setOrders(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('Error fetching orders:', e);
      showSnackbar('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, []);

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  // ---------- Delete ----------
  const openDeleteDialog = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    try {
      setDeleting(true);
      const url = authService.pathUrl('DELETE', `/payment/${orderToDelete.id}`);
      await authService.deleteAuth(url);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      showSnackbar('Order deleted successfully', 'success');
      await loadData();
    } catch (e) {
      console.error('Error deleting order:', e);
      showSnackbar('Failed to delete order', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Edit ----------
  const openEditDialog = (order) => {
    setEditingOrder({ ...order });
    setEditDialogOpen(true);
  };

  const handleEditChange = (field) => (e) =>
    setEditingOrder((prev) => ({ ...prev, [field]: e.target.value }));

  const handleUpdateSubmit = async () => {
    if (!editingOrder) return;
    try {
      setSaving(true);
      const url = authService.pathUrl('PUT', `/payment/orders/${editingOrder.id}`);
      await authService.putAuth(url, editingOrder);
      setOrders((prev) =>
        prev.map((o) => (o.id === editingOrder.id ? { ...o, ...editingOrder } : o))
      );
      setEditDialogOpen(false);
      setEditingOrder(null);
      showSnackbar('Order updated successfully', 'success');
    } catch (e) {
      console.error('Error updating order:', e);
      showSnackbar('Failed to update order', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Filtering ----------
  const filteredOrders = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return (orders || []).filter((order) => {
      if (statusFilter !== 'all' && String(order.status || '').toLowerCase() !== statusFilter) {
        return false;
      }
      if (!term) return true;
      return [
        order.id,
        order.loginId,
        order.masterCode,
        order.planName,
        order.paymentId,
        order.currency,
        order.priceAmount,
        order.paidAmount,
        order.discountAmount,
        order.status,
      ]
        .filter((v) => v != null && v !== '')
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [orders, searchText, statusFilter]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = orders.length;
    const paid = orders.filter((o) => String(o.status).toLowerCase() === 'paid').length;
    const pending = orders.filter((o) => String(o.status).toLowerCase() === 'pending').length;
    const revenue = orders
      .filter((o) => String(o.status).toLowerCase() === 'paid')
      .reduce((sum, o) => sum + Number(o.paidAmount || 0), 0);
    return { total, paid, pending, revenue };
  }, [orders]);

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchText || statusFilter !== 'all';

  // ---------- Loading ----------
  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      icon={<ReceiptIcon />}
      title="Orders"
      subtitle="Payments, plans and transaction history"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Orders' }]}
      actions={
        <Tooltip title="Refresh">
          <IconButton
            onClick={loadData}
            disabled={loading}
            sx={{
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Orders" value={stats.total} icon={<ReceiptIcon />} color="#2563eb" />
        <StatCard label="Paid" value={stats.paid} icon={<PaidIcon />} color="#059669" />
        <StatCard label="Pending" value={stats.pending} icon={<PendingIcon />} color="#d97706" />
        <StatCard
          label="Revenue"
          value={formatMoney(stats.revenue, orders[0]?.currency || 'INR')}
          icon={<TrendingIcon />}
          color="#7c3aed"
        />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search by ID, plan, payment ID, master code..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
            Clear
          </Button>
        )}
      </Paper>

      {/* Results summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing <strong>{filteredOrders.length}</strong> of{' '}
        <strong>{orders.length}</strong>{' '}
        {orders.length === 1 ? 'order' : 'orders'}
        {searchText && ` matching "${searchText}"`}
      </Typography>

      {/* Table / Empty */}
      {filteredOrders.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<ReceiptIcon />}
            title={orders.length === 0 ? 'No orders yet' : 'No matches'}
            subtitle={
              orders.length === 0
                ? 'Orders will appear here once payments are made.'
                : 'Try a different search term or clear the filters.'
            }
            actionLabel={hasActiveFilters ? 'Clear Filters' : undefined}
            onAction={clearFilters}
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 1150 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell>Order</TableCell>
                  <TableCell>Login / Master</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Payment ID</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => {
                  const meta = statusMeta(order.status);
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            sx={{
                              bgcolor: meta.bg,
                              color: meta.color,
                              width: 38,
                              height: 38,
                              '& svg': { fontSize: 18 },
                            }}
                          >
                            {meta.icon}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700}>
                              #{String(order.id).padStart(5, '0')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.currency || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            Login #{order.loginId ?? '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {order.masterCode || '—'}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {order.planName || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            bgcolor: 'grey.100',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                            display: 'inline-block',
                          }}
                        >
                          {order.paymentId || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatMoney(order.priceAmount, order.currency)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {formatMoney(order.paidAmount, order.currency)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        {Number(order.discountAmount) > 0 ? (
                          <Typography variant="body2" color="secondary.main" fontWeight={600}>
                            -{formatMoney(order.discountAmount, order.currency)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={meta.label}
                          size="small"
                          icon={meta.icon}
                          sx={{
                            bgcolor: meta.bg,
                            color: meta.color,
                            fontWeight: 700,
                            border: 'none',
                            '& .MuiChip-icon': { color: meta.color },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{formatDate(order.createdAt)}</Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit Order">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDialog(order)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Order">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(order)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ---------- Delete confirmation ---------- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden', maxWidth: 420 } }}
      >
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)',
            color: '#fff',
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.18)',
              width: 52,
              height: 52,
              mx: 'auto',
              mb: 1,
              backdropFilter: 'blur(6px)',
            }}
          >
            <Delete />
          </Avatar>
          <Typography variant="h6" fontWeight={700}>
            Delete Order?
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Order #{String(orderToDelete?.id || '').padStart(5, '0')}
          </Typography>
        </Box>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. Are you sure you want to permanently delete this
            order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Edit dialog ---------- */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 55%, #60a5fa 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                width: 52,
                height: 52,
                backdropFilter: 'blur(6px)',
              }}
            >
              <Edit />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Update Order
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Order #{String(editingOrder?.id || '').padStart(5, '0')}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Plan Name"
                value={editingOrder?.planName || ''}
                onChange={handleEditChange('planName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Payment ID"
                value={editingOrder?.paymentId || ''}
                onChange={handleEditChange('paymentId')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Master Code"
                value={editingOrder?.masterCode || ''}
                onChange={handleEditChange('masterCode')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Currency"
                value={editingOrder?.currency || ''}
                onChange={handleEditChange('currency')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    fontSize: 10.5,
                  }}
                >
                  Amounts
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Price"
                type="number"
                value={editingOrder?.priceAmount ?? ''}
                onChange={handleEditChange('priceAmount')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Paid"
                type="number"
                value={editingOrder?.paidAmount ?? ''}
                onChange={handleEditChange('paidAmount')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Discount"
                type="number"
                value={editingOrder?.discountAmount ?? ''}
                onChange={handleEditChange('discountAmount')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={editingOrder?.status || ''}
                onChange={handleEditChange('status')}
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Edit />}
          >
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Snackbar ---------- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default OrderView;